import React from "react";
import Presence from "src/constants/presence";
import AppStatus from "src/constants/AppStatus";
import RPC from "src/modules/rpc";
import { RPCRequests, Message } from "src/modules/communication-protobuf";
import debug from "src/modules/debug-logger";
import environment, { electronOnly } from "src/utils/environment";
import appDebugger from "src/modules/debug/AppDebugger";
import ParentStationRtcManager from "src/modules/parent-station/lib/ParentStationRtcManager";
import PingService from "src/modules/parent-station/lib/PingService";
import createActivityEmitter from "src/modules/audio-processing/createActivityEmitter";
import createErrorManager from "src/modules/parent-station/lib/createErrorManager";
import createVideoErrorManager from "src/modules/parent-station/lib/createVideoErrorManager";
import createFlashlightManager from "src/modules/parent-station/lib/createFlashlightManager";
import { Activities } from "src/modules/activity-log/activity";
import MultiBabyManager from "src/modules/parent-station/MultiBabyManager";
import { PeerToPeerMessenger } from "src/modules/messenger/PeerToPeerMessenger";
import { Controls } from "src/utils/createControls";
import { store } from "src/store/store";
import { lastRoleChanged } from "src/duck/actions";
import createVideoModeUpdater from "../lib/createVideoModeUpdater";
import StopLullabyPlayerDialog from "../components/StopLullabyPlayerDialog";
import SelectBabyStation from "../scenes/SelectBabyStation";
import Monitors from "../scenes/Monitors";
import Alert, { AlertsById } from "./alerts";
import ParentStationContext from "./Context";
import MonitoringHelp from "../scenes/MonitoringHelp";
import monitoringRestorer from "src/modules/monitoring/monitoringRestorer";

const remote = window.require("@electron/remote");
const log = window.require("electron-log");
const WAIT_FOR_RTC_TIMEOUT = 9 * 1000;

const defaultStationProps = {
  activity: Activities.SLEEPING, // |SLEEPING, AWAKE, MOTION|,
  batteryStatus: null,
  commandPlayerState: {},
  error: null,
  flashlightManager: null,
  hadLowBattery: false,
  cameraAndFlashlightState: {
    flashlightAvailable: false,
    flashlightEnabled: false,
    flashlightAdjustable: false,
    flashlightIntensity: -1,
    lowLightDetected: false,
    lowLightMode: "LL_OFF"
  },
  lastNoise: null,
  lastMotion: null,
  localVideoStream: null,
  motionDetectorState: "UNKNOWN",
  mode: "audio",
  monitoringId: null,
  monitoringStartTime: null,
  noiseMeter: null,
  pushToTalkState: "off",
  opusRecorder: null,
  remoteSettings: {},
  remoteVideoStream: null,
  rpc: null,
  seeMeState: "off", // |off, on, starting|
  senderStationConfiguration: null,
  status: "selecting", // |selecting, connecting, connected, disconnecting|
  videoError: null,
  lullabyPlayerState: null,
  resumeVideoMode: false,
  isMultiRoom: false,
  videoMode: null
};

const notToResetInitialState = {
  alert: {
    id: null,
    props: {}
  },
  previousBabyStation: null
};
export default class ParentStation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      babyStations: {},
      babyStationSelect: new Controls({
        onUpdate: (babyStationSelect) => this.setState({ babyStationSelect }),
        isOpen: true
      }),
      multiBabyManager: new Controls({
        onUpdate: (multiBabyManager) => this.setState({ multiBabyManager })
      }),
      primaryStation: {},
      hasConnectedStations: false,
      isConnecting: false,
      showTilesView: false,
      renderLowBatteryDialog: false,
      lowBatteryDialogProps: {
        deviceName: null,
        avatarId: null,
        playAlert: false
      },
      muted: false,
      isStopLullabyPlayerDialogOpen: false,
      renderSeeMeButton: false,
      ...notToResetInitialState,
      toggleSeeMeButton: this.toggleSeeMeButton,
      changeCamera: this.changeCamera,
      handleConnect: this.handleConnect,
      updatePrimaryStation: this.updatePrimaryStation,
      getMonitoredAvatar: this.getMonitoredAvatar,
      isBabyStationFamilyDevice: this.isBabyStationFamilyDevice,
      handleMicSensitivityChange: this.handleMicSensitivityChange,
      playCommand: this.playCommand,
      resetCommandPlayer: this.resetCommandPlayer,
      toggleMute: this.toggleMute,
      setVolume: this.setVolume,
      stopSeeMe: this.stopSeeMe,
      stopCommand: this.stopCommand,
      stopMonitoring: this.stopMonitoring,
      askToStopMonitoringForAll: this.askToStopMonitoringForAll,
      stopMonitoringForAll: this.stopMonitoringForAll,
      switchAvatar: this.handleAvatarChange,
      toggleSeeMe: this.toggleSeeMe,
      togglePushToTalk: this.togglePushToTalk,
      toggleMonitoringMode: this.toggleMonitoringMode,
      togglePurchaseFlow: this.togglePurchaseFlow,
      toggleTilesView: this.toggleTilesView,
      onVideoQualityChange: this.onVideoQualityChange,
      handleMotionSensitivityChange: this.handleMotionSensitivityChange,
      toggleLowBatteryDialog: this.toggleLowBatteryDialog,
      handleLowLightModeChange: this.handleLowLightModeChange,
      videoUpdater: this.videoUpdater
    };
  }

  reorderStations = true;
  recoveredStations = {};
  stopLullabyPlayerDialogState = null;
  lullabyPlayersToStop = null;
  lullabyPlayerStationId = null;
  videoTrack = null;
  errorManager = null;
  stopSeeMePromise = null;
  clearDataChannleConnectionChecker = null;
  clearStartVideoConnectionChecker = null;
  isWaitingForStartVideoResponse = false;
  isWaitingForStopVideoResponse = false;

  rpcs = {};
  rtcManagers = {};
  activityEmitters = {};
  pingServices = {};
  noiseMeters = {};
  errorManagers = {};
  videoErrorManagers = {};
  videoUpdater = createVideoModeUpdater(
    () => this.state.babyStations,
    (jid, mode) => {
      if (jid && mode) this.updateStation(jid, { videoMode: mode });
      else {
        Object.keys(this.state.babyStations).forEach((jid) => {
          this.updateStation(jid, { videoMode: null });
        });
      }
    }
  );

  componentDidMount = () => {
    const { messenger } = this.props;
    store.dispatch(lastRoleChanged("parent"));
    messenger.setPresence(Presence.RECEIVER);
    this.peerToPeerMessenger = new PeerToPeerMessenger({ messenger });
    this.videoUpdater.destroy();
  };

  componentDidUpdate(prevProps, prevState) {
    const {
      babyStations,
      showTilesView,
      babyStationSelect,
      hasConnectedStations,
      primaryStation,
      isConnecting,
      multiBabyManager
    } = this.state;

    const { messenger, appStatus, stationsByJid } = this.props;
    Object.values(stationsByJid).forEach((s) => {
      if (s?.status === Presence.SENDER_CONNECTED && !this.recoveredStations[s.jid]) this.recoverMonitoring(s.jid);
    });

    const monitoringEnded = Object.keys(prevState.babyStations).length >= 1 && Object.keys(babyStations).length === 0;

    const hasLastStation = Object.keys(prevState.babyStations).length >= 1 && Object.keys(babyStations).length === 1;

    if (hasLastStation && showTilesView) {
      this.toggleTilesView();
      if (!multiBabyManager.isOpen) multiBabyManager.open();
    }

    if (monitoringEnded) {
      this.setState({
        primaryStation: {},
        hasConnectedStations: false,
        muted: false
      });
      this.videoUpdater.destroy();
      messenger.setPresence(Presence.RECEIVER);
      debug.parentStation("Stopped monitoring of all stations");
      this.reorderStations = true;
      this.lullabyPlayerStationId = null;
      appDebugger.parentRtcManagers = null;
    }

    if (!hasConnectedStations && !babyStationSelect.isOpen && !isConnecting) {
      babyStationSelect.open();
    }

    if (this.reorderStations && hasConnectedStations && !monitoringEnded) {
      const { [primaryStation.jid]: first, ...rest } = babyStations;
      this.setState({ babyStations: { [first.jid]: first, ...rest } });
      this.reorderStations = false;
    }

    if (appStatus !== prevProps.appStatus) {
      if (appStatus === AppStatus.STARTED && Object.keys(this.pingServices).length !== 0) {
        this.pingServices = Object.values(this.pingServices).reduce((all, one) => {
          if (one) one.shouldSynchronizeOnNextPingPong = true;
          return all;
        }, this.pingServices);
      }
      if (Object.keys(this.errorManagers).length !== 0) {
        Object.values(this.errorManagers).forEach((manager) => {
          if (manager) {
            manager.onAppStatusChange(appStatus);
          }
        });
      }
      if (Object.keys(this.rtcManagers).length !== 0) {
        Object.values(this.rtcManagers).forEach((manager) => {
          if (manager) {
            manager.onAppStatusChange(appStatus);
          }
        });
      }
    }
  }

  componentWillUnmount = () => {
    const { messenger } = this.props;
    messenger.setPresence(Presence.NOT_CHOSEN_YET);

    electronOnly(() => {
      this.powerSaveBlockerId && remote.powerSaveBlocker.stop(this.powerSaveBlockerId);
      this.powerSaveBlockerId = null;
    });
  };

  updateStation = (stationId, update, withPromise = false) => {
    const { jid: primaryStationJid } = this.state.primaryStation;
    this.setState(({ babyStations }) => ({
      babyStations: {
        ...babyStations,
        [stationId]: {
          ...babyStations[stationId],
          ...update
        }
      }
    }));
    if (primaryStationJid === stationId) this.updatePrimaryStation(update);
    if (withPromise) return new Promise((resolve) => resolve());
  };

  updatePrimaryStation = (update, withPromise = false) => {
    this.setState(({ primaryStation }) => ({
      primaryStation: { ...primaryStation, ...update }
    }));
    if (withPromise) return new Promise((resolve) => resolve());
  };

  getStationById = (id) => this.state.babyStations[id];

  initializePrimaryStation = (stations) => {
    const { allAvatars } = this.props;
    const primaryStation = Object.values(stations).reduce((a, b) => {
      return new Date(allAvatars[a.avatarId]?.birthDate) > new Date(allAvatars[b.avatarId]?.birthDate) ? a : b;
    });
    this.updatePrimaryStation(primaryStation);
  };

  recoverMonitoring = async (jid) => {
    const sessionsToRecover = monitoringRestorer.getRecoverableSessions();
    if (sessionsToRecover.length === 0) return;

    const stationToRecover = sessionsToRecover.find((s) => s.jid === jid);

    if (stationToRecover) {
      this.recoveredStations[jid] = true;
      await this.handleConnect([stationToRecover]);
    }
  };

  checkForMultiRoom = (newStations = {}) => {
    const stations = { ...this.state.babyStations, ...newStations };
    const counts = {};
    Object.values(stations).forEach((station) => {
      counts[station.avatarId] = {
        duplicates: (counts[station.avatarId]?.duplicates || 0) + 1,
        jids: [...(counts[station.avatarId]?.jids || []), station.jid]
      };
    });

    Object.values(counts).forEach(({ duplicates, jids }) => {
      if (duplicates > 1) {
        jids.forEach((jid) => {
          this.updateStation(jid, {
            isMultiRoom: true
          });
        });
      } else {
        jids.forEach((jid) => {
          this.updateStation(jid, {
            isMultiRoom: false
          });
        });
      }
    });
  };

  setMessageListeners = (jid) => {
    this.peerToPeerMessenger.on(jid, Message.BabyStationState.name, this.handleBabyStationStateMessage);
    this.peerToPeerMessenger.on(jid, Message.BatteryStatus.name, this.updateBatteryStatus);
    this.peerToPeerMessenger.on(jid, Message.DataRemoteSettings.name, this.handleDataRemoteSettingsMessage);
    this.peerToPeerMessenger.on(jid, Message.StopVideoBroadcasting.name, this.handleStopVideoBroadcastingMessage);
    this.peerToPeerMessenger.on(jid, Message.CameraAndFlashlightState.name, this.handleCameraAndFlashlightStateMessage);
  };

  /* Connection */
  handleConnect = async (stations) => {
    const { babyStationSelect, multiBabyManager, primaryStation } = this.state;
    const { messenger, videoQuality, allAvatars, lullabyPlayers } = this.props;
    this.setState({ isConnecting: true });
    const babyStations = stations.reduce((stations, station) => {
      stations[station.jid] = { ...station, ...defaultStationProps };
      return stations;
    }, {});

    const babyStationsArray = Object.values(babyStations);
    const errorStations = {};
    const connectedStations = {};
    await Promise.all(
      babyStationsArray.map(async (babyStation, i) => {
        try {
          const { jid, avatarId } = babyStation;
          this.rpcs[jid] = new RPC({ messenger, peerJid: jid });
          this.rpcs[jid].on(RPCRequests.StopMonitoringFromBaby.name, this.handleStopMonitoringFromBabyRPCRequest);
          const request = RPCRequests.StartMonitoring.create({
            avatarId,
            parentStationState: {
              videoQuality
            }
          });
          const response = await this.rpcs[jid].call(request, jid, {
            timeout: 7000
          });
          let {
            babyStationState,
            lastNoise,
            lastMotion,
            motionDetectorState,
            monitoringTime,
            monitoringId,
            senderStationConfiguration,
            remoteSettings,
            batteryStatus,
            resultCode
          } = response;

          this.recoveredStations[jid] = true;
          monitoringRestorer.setRecoverableSessions({ monitoringId, jid });

          debug.parentStation("Received StartMonitoringRPCResponse: ", response);

          this.pingServices[jid] = new PingService({
            synchronizeStations: this.synchronizeStations
          });

          this.rtcManagers[jid] = new ParentStationRtcManager({
            getActivityEmitter: () => this.activityEmitters[jid],
            pingService: this.pingServices[jid],
            messenger,
            onRecreateConnection: () => this.updateStation(jid, { seeMeState: "off" })
          });

          this.rtcManagers[jid].on("media-track", (e) => this.onTrack(jid, e));

          const { opusPlayer, getPeerConnection } = await this.rtcManagers[jid].initializeConnection(jid);
          this.noiseMeters[jid] = opusPlayer.noiseMeter;

          this.errorManagers[jid] = createErrorManager({
            peerJid: jid,
            rpc: this.rpcs[jid],
            onChange: (error) => {
              if (error == null) {
                this.pingServices[jid].shouldSynchronizeOnNextPingPong = true;
              }
              this.updateStation(jid, { error });
            },
            messenger: this.peerToPeerMessenger,
            pingService: this.pingServices[jid]
          });

          this.activityEmitters[jid] = createActivityEmitter({
            noiseMeter: this.noiseMeters[jid],
            durationToSleep: 2000
          });
          this.activityEmitters[jid].on("activity-change", (activity) => this.handleActivityChange(jid, activity));

          const flashlightManager = createFlashlightManager({
            getIntensityFromState: () => this.getStationById(jid)?.cameraAndFlashlightState?.flashlightIntensity,
            peerToPeerMessenger: this.peerToPeerMessenger,
            messenger,
            peerJid: jid,
            onChange: (cameraAndFlashlightStateChange) =>
              this.onFlashLightIntensityChange(jid, cameraAndFlashlightStateChange)
          });

          if (senderStationConfiguration.numberOfCameras > 0) {
            this.videoErrorManagers[jid] = createVideoErrorManager({
              getPeerConnection,
              onChange: (videoError) => this.updateStation(jid, { videoError })
            });
          }
          if (batteryStatus?.state !== "CHARGING") {
            this.checkForLowBattery(jid, batteryStatus?.level);
          }

          if (babyStationState) {
            lastNoise = babyStationState.lastNoise;
            lastMotion = babyStationState.lastMotion;
            remoteSettings = babyStationState.remoteSettings;
            batteryStatus = babyStationState.batteryStatus;
            motionDetectorState = babyStationState.motionDetectorState;
          }

          if (
            lullabyPlayers &&
            this.isBabyStationFamilyDevice({ station: babyStation }) &&
            senderStationConfiguration.supportedFeatures.includes("LULLABIES")
          ) {
            lullabyPlayers[i].initialize({
              monitoringId,
              messenger,
              initialState: babyStationState.lullabyPlayerState,
              babyJid: jid,
              rpc: this.rpcs[jid]
            });
          }

          const currentStation = {
            ...babyStation,

            monitoringTime,
            monitoringId,
            senderStationConfiguration,
            remoteSettings,
            batteryStatus,
            resultCode,
            flashlightManager,
            lastNoise: lastNoise <= 0 ? null : Date.now() - lastNoise,
            lastMotion: lastMotion <= 0 ? null : Date.now() - lastMotion,
            monitoringStartTime: Date.now() - monitoringTime,
            motionDetectorState: motionDetectorState?.state || "UNKNOWN",
            noiseMeter: this.noiseMeters[jid],
            rpc: this.rpcs[jid],
            status: "connected",
            lullabyPlayerState: babyStationState.lullabyPlayerState,
            cameraAndFlashlightState: babyStationState.cameraAndFlashlightState,
            lullabyPlayerId: i
          };
          if (this.state.muted) this.setVolume(currentStation.jid, 0);
          this.setMessageListeners(jid);
          this.updateStation(jid, currentStation);
          connectedStations[jid] = currentStation;
        } catch (error) {
          log.error(error);
          errorStations[babyStation.jid] = babyStation;
        }
      })
    );

    if (Object.keys(errorStations).length === babyStationsArray.length) {
      Object.values(errorStations).forEach((station) => {
        this.updateStation(station.jid, {
          ...station,
          error: true
        });
        this.resetStation(station.jid);
      });
      this.setState({ isConnecting: false });
      this.openAlert(
        environment.supportsMultibaby ? AlertsById.MULTI_CONNECTION_FAILURE : AlertsById.CONNECTION_FAILURE,
        environment.supportsMultibaby
          ? {
              errorStations,
              avatars: allAvatars
            }
          : {}
      );
      return;
    }
    if (Object.keys(errorStations).length !== 0) {
      Object.values(errorStations).forEach((station) => {
        this.updateStation(station.jid, {
          ...station,
          error: true
        });
        this.resetStation(station.jid);
      });
      this.openAlert(AlertsById.MULTI_CONNECTION_FAILURE, {
        errorStations,
        avatars: allAvatars
      });
    }

    const hasPrimaryStation = primaryStation && Object.keys(primaryStation).length !== 0;
    if (!hasPrimaryStation) {
      this.initializePrimaryStation(connectedStations);
    }

    appDebugger.parentRtcManagers = this.rtcManagers;
    if (appDebugger.state === "enabled") {
      appDebugger.startParentDebug();
    }
    this.checkForMultiRoom(connectedStations);
    this.setState({ hasConnectedStations: true, isConnecting: false });
    babyStationSelect.close();
    multiBabyManager.open();

    messenger.setPresence(Presence.RECEIVER_CONNECTED);
    this.videoUpdater.init();

    electronOnly(() => {
      this.powerSaveBlockerId = remote.powerSaveBlocker.start("prevent-display-sleep");
    });
  };

  stopMonitoring = async (stationId) => {
    const { primaryStation } = this.state;
    let station = stationId ? this.getStationById(stationId) : primaryStation;
    if (!station) station = primaryStation;
    const { appStatus, appFailure } = this.props;
    this.updateStation(station.jid, { status: "disconnecting" });

    if (appStatus === AppStatus.CONNECTION_LOST) {
      appFailure();
      return;
    }

    try {
      let askToStopLullabyPlayer = false;

      const request = RPCRequests.StopMonitoring.create();
      const response = await this.rpcs[station.jid].call(request, station.jid);

      askToStopLullabyPlayer = response.askToStopLullabyPlayer;

      if (this.lullabyPlayersToStop && askToStopLullabyPlayer) {
        this.lullabyPlayersToStop[station.jid] = station;
      }

      if (askToStopLullabyPlayer && !this.lullabyPlayersToStop) {
        this.askToStopLullabyPlayer({
          monitoringId: station.monitoringId,
          babyJid: station.jid
        });
      }

      this.resetStation(station.jid);
    } catch (error) {
      log.error(error);
      this.resetStation(station.jid);
    }
    debug.parentStation("Stopped monitoring station ", station.jid);
  };

  askToStopMonitoringForAll = () => {
    this.openAlert(AlertsById.STOP_MONITORING_ALL_STATIONS, {
      onOk: () => {
        this.dismissAlert();
        this.stopMonitoringForAll();
      }
    });
  };

  stopMonitoringForAll = async () => {
    const { babyStations } = this.state;
    this.lullabyPlayersToStop = {};
    await Promise.all(
      Object.values(babyStations).map(async (station) => {
        await this.stopMonitoring(station.jid);
      })
    );
    if (Object.keys(this.lullabyPlayersToStop).length !== 0) {
      this.askToStopLullabyPlayer();
    }
  };

  askToStopLullabyPlayer = ({ monitoringId, babyJid } = {}) => {
    if (!this.lullabyPlayersToStop) {
      this.stopLullabyPlayerDialogState = {
        monitoringId,
        babyJid
      };
    }

    this.setState({
      isStopLullabyPlayerDialogOpen: true
    });
  };

  dismissStopLullabyPlayerDialog = async ({ shouldStopPlaying }) => {
    const { messenger } = this.props;
    const { monitoringId, babyJid } = this.stopLullabyPlayerDialogState || {};

    this.setState({ isStopLullabyPlayerDialogOpen: false });

    const stations = this.lullabyPlayersToStop
      ? Object.values(this.lullabyPlayersToStop)
      : [{ jid: babyJid, monitoringId }];
    if (shouldStopPlaying) {
      const rpc = new RPC({ messenger });
      try {
        for (let station of stations) {
          await rpc.call(
            RPCRequests.StopLullabyAfterMonitoring.create({
              monitoringId: station.monitoringId
            }),
            station.jid
          );
        }
        rpc.destroy();
      } catch (error) {
        debug.parentStation("StopLullabyAfterMonitoringRpcRequest failed", error);
      }
    }
    this.stopLullabyPlayerDialogState = null;
    this.lullabyPlayersToStop = null;
  };

  handleStopMonitoringFromBabyRPCRequest = (request, from, sendResponse) => {
    debug.parentStation("Received handleStopMonitoringFromBabyRPCRequest from", from);

    const { reason } = request;
    sendResponse(null, "xmpp");
    const station = this.getStationById(from);
    const avatar = this.props.allAvatars?.[station?.avatarId];

    const alertProps =
      Object.keys(this.state.babyStations).length > 1
        ? {
            deviceName: station?.name,
            avatarName: avatar?.name
          }
        : {};
    const alert =
      reason === AlertsById.STOP_REASON_SCHEDULER_STOP
        ? AlertsById.STOP_REASON_SCHEDULER_STOP
        : AlertsById.BABY_STOPPED;
    this.openAlert(alert, alertProps);
    this.resetStation(from);
  };

  handleBabyStationStateMessage = (babyStationState, from) => {
    const station = this.getStationById(from);
    debug.parentStation("Received babyStationState", babyStationState);
    if (babyStationState.lullabyPlayerState && this.props.lullabyPlayers?.[station.lullabyPlayerId]) {
      this.props.lullabyPlayers[station.lullabyPlayerId].stateUpdateHandler(babyStationState.lullabyPlayerState);
    }
    const {
      lastNoise = 0,
      lastMotion = 0,
      remoteSettings,
      batteryStatus,
      cameraAndFlashlightState,
      motionDetectorState,
      lullabyPlayerState
    } = babyStationState;
    this.handleDataRemoteSettingsMessage(remoteSettings, from);
    const update = {
      lastNoise: lastNoise <= 0 ? null : Date.now() - lastNoise,
      lastMotion: lastMotion <= 0 ? null : Date.now() - lastMotion
    };
    if (batteryStatus) update.batteryStatus = batteryStatus;
    if (cameraAndFlashlightState) update.cameraAndFlashlightState = cameraAndFlashlightState;
    if (motionDetectorState?.state) update.motionDetectorState = motionDetectorState.state;
    if (lullabyPlayerState) update.lullabyPlayerState = lullabyPlayerState;
    this.updateStation(from, update);
  };

  onFlashLightIntensityChange(jid, { enabled, intensity }) {
    const babyStation = this.getStationById(jid);
    if (!babyStation) return;
    this.updateStation(jid, {
      cameraAndFlashlightState: {
        ...babyStation.cameraAndFlashlightState,
        flashlightEnabled: enabled,
        flashlightIntensity: intensity
      }
    });
  }

  onVideoQualityChange = async (videoQuality) => {
    const { primaryStation } = this.state;
    this.rtcManagers[primaryStation.jid].updatePeersVideoTrack(primaryStation.jid);
    this.synchronizeStations({ videoQuality });
  };

  synchronizeStations = async ({ videoQuality = this.props.videoQuality } = {}) => {
    const { babyStations } = this.state;
    await Promise.all(
      Object.values(babyStations).map(async (babyStation) => {
        const supportedFeatures = babyStation.senderStationConfiguration?.supportedFeatures;
        if (supportedFeatures && supportedFeatures.includes("STATION_SYNC") && this.rpcs[babyStation.jid]) {
          try {
            debug.parentStation("Sending SyncFromPSRPCRequest");
            const request = RPCRequests.SyncFromPS.create({
              parentStationState: {
                videoQuality
              }
            });
            const syncFromBSRPCResponse = await this.rpcs[babyStation.jid].call(request, babyStation.jid);
            this.handleBabyStationStateMessage(syncFromBSRPCResponse.babyStationState, babyStation.jid);
          } catch (error) {
            log.error(error);
          }
        }
      })
    );
  };
  /* Connection - END */

  /* Video Mode */
  onTrack = (jid, e) => {
    debug.parentStation("onTrack fired", e);
    if (e.track.kind === "video") {
      const remoteVideoStream = e.streams[0];
      this.updateStation(jid, { remoteVideoStream });
    }
  };

  startVideoMode = async (jid) => {
    try {
      this.updateStation(jid, { mode: "video", resumeVideoMode: false });
      const { promise, clear } = this.rtcManagers[jid].waitForConnectedState(WAIT_FOR_RTC_TIMEOUT, jid);
      this.clearStartVideoConnectionChecker = clear;
      const canContinue = await promise;
      this.clearStartVideoConnectionChecker = null;

      if (!canContinue) {
        this.updateStation(jid, {
          mode: "audio",
          remoteVideoStream: null
        });
        return;
      }

      const { promise: dataChannelpromise, clear: dataChannelClear } = this.rtcManagers[jid].waitForCommDataChannel(
        WAIT_FOR_RTC_TIMEOUT,
        jid
      );

      this.clearDataChannleConnectionChecker = dataChannelClear;
      await dataChannelpromise;
      this.clearDataChannleConnectionChecker = null;

      const request = RPCRequests.StartVideoBroadcasting.create();
      this.isWaitingForStartVideoResponse = true;
      const { result, cameraAndFlashlightState } = await this.rpcs[jid].call(request, jid);
      this.isWaitingForStartVideoResponse = false;

      if (result === "NOT_AVAILABLE") {
        this.openAlert(AlertsById.BABY_CAMERA_NOT_AVAILABLE);
        this.updateStation(jid, {
          mode: "audio"
        });
      }
      this.videoErrorManagers[jid].start();
      this.updateStation(jid, { cameraAndFlashlightState });
    } catch (error) {
      log.error("startVideoMode() failed", error);
      this.isWaitingForStartVideoResponse = false;
      if (this.state.babyStations[jid]?.status === "disconnecting") {
        return;
      }
      this.openAlert(AlertsById.VIDEO_MODE_START_FAILURE);
      this.updateStation(jid, {
        mode: "audio",
        remoteVideoStream: null
      });
    }
  };
  stopVideoMode = async (jid, shouldResume = false) => {
    try {
      const { seeMeState, cameraAndFlashlightState, flashlightManager } = this.getStationById(jid);
      this.updateStation(jid, {
        mode: "audio",
        remoteVideoStream: null,
        seeMeState: "off",
        resumeVideoMode: shouldResume
      });
      if (cameraAndFlashlightState.flashlightEnabled) {
        flashlightManager.turnOff();
      }
      if (this.clearStartVideoConnectionChecker) {
        this.clearStartVideoConnectionChecker();
        this.clearStartVideoConnectionChecker = null;
        return;
      }
      if (this.clearDataChannleConnectionChecker) {
        this.clearDataChannleConnectionChecker();
        this.clearDataChannleConnectionChecker = null;
        return;
      }
      this.isWaitingForStopVideoResponse = true;

      this.videoErrorManagers[jid].stop();

      const request = RPCRequests.StopVideoBroadcasting.create();
      await this.rpcs[jid].call(request, jid);
      this.isWaitingForStopVideoResponse = false;

      if (seeMeState === "on") {
        this.stopSeeMePromise = new Promise((resolve) => setTimeout(resolve, 5000));
        await this.stopSeeMePromise;
        this.stopSeeMePromise = this.stopSeeMe(jid);
        await this.stopSeeMePromise;
      }
    } catch (error) {
      this.isWaitingForStopVideoResponse = false;
      log.error(error);
    }
  };

  toggleMonitoringMode = async (jid, shouldResume) => {
    const { mode } = this.getStationById(jid) || {};
    if (
      this.isWaitingForStartVideoResponse ||
      this.isWaitingForStopVideoResponse ||
      this.state.babyStations[jid]?.status === "disconnecting"
    )
      return;
    mode === "audio" ? this.startVideoMode(jid) : this.stopVideoMode(jid, shouldResume);
  };
  /* END Video Mode END */

  changeCamera = async (cameraId = "") => {
    const { primaryStation } = this.state;
    const request = RPCRequests.ChangeCamera.create({ cameraId });
    const { cameraAndFlashlightState, result } = await this.rpcs[primaryStation.jid].call(request, primaryStation.jid);
    try {
      if (result === "FAILURE") throw new Error("Request for camera change failed");
      this.updateStation(primaryStation.jid, { cameraAndFlashlightState });
    } catch (error) {
      log.error(error);
      this.openAlert(AlertsById.GENERIC_FAILURE);
    }
  };

  handleCameraAndFlashlightStateMessage = (cameraAndFlashlightState, from) => {
    debug.parentStation("Received cameraAndFlashlightState: ", cameraAndFlashlightState);
    this.updateStation(from, { cameraAndFlashlightState });
  };

  /* See Me */
  startSeeMe = async (jid) => {
    const { app } = this.props;
    this.updateStation(jid, { seeMeState: "starting" });

    try {
      const status = await app.permissions.getMediaAccessStatus("camera");
      let hasPermission = status === "granted";

      if (status === "not-determined") {
        hasPermission = await app.permissions.resolveMediaPermission("camera");
      }
      if (!hasPermission) {
        status === "denied" && app.permissions.openDialog("camera");
        this.updateStation(jid, { seeMeState: "off" });
        return;
      }

      const localVideoStream = await this.rtcManagers[jid].initializeVideoStream();

      if (localVideoStream == null) {
        this.updateStation(jid, {
          seeMeState: "off"
        });
        this.openAlert(AlertsById.LOCAL_CAMERA_NOT_AVAILABLE);
        return;
      }

      const request = RPCRequests.StartVideoReceiving.create();
      const { result, previewAspectRatio } = await this.rpcs[jid].call(request, jid);

      if (result === "ALREADY_RUNNING") {
        this.rtcManagers[jid].destroyVideoStream();
        this.openAlert(AlertsById.SEE_ME_ALREADY_RUNNING);
        this.updateStation(jid, {
          seeMeState: "off"
        });
        return;
      }

      await this.rtcManagers[jid].startSendingVideoTo(jid);
      this.updateStation(jid, {
        localVideoStream,
        seeMeState: "on",
        remotePreviewAspectRatio: previewAspectRatio
      });
    } catch (error) {
      this.rtcManagers[jid].destroyVideoStream();
      log.error(error);
      this.openAlert(AlertsById.SEE_ME_START_FAILURE);
      this.updateStation(jid, {
        seeMeState: "off"
      });
    }
  };
  stopSeeMe = async (jid) => {
    this.updateStation(jid, { seeMeState: "off" });
    try {
      const request = RPCRequests.StopVideoReceiving.create();
      await this.rpcs[jid].call(request, jid);
    } catch (error) {
      log.error(error);
    }
    try {
      await this.rtcManagers[jid].stopSendingVideoToBaby(jid);
    } catch (error) {
      log.error(error);
    }
    this.stopSeeMePromise = null;
  };
  toggleSeeMe = async (stationId) => {
    const { primaryStation } = this.state;
    let station = stationId ? this.getStationById(stationId) : primaryStation;
    if (!station) station = primaryStation;
    const { seeMeState, jid } = station;

    if (seeMeState === "starting") return;
    if (seeMeState === "off") {
      if (this.stopSeeMePromise) {
        this.updateStation(jid, { seeMeState: "starting" });
        try {
          await this.stopSeeMePromise;
        } catch (error) {
          log.error("Stop see me failed", error);
        }
      }
      this.startSeeMe(jid);
    } else if (seeMeState === "on") {
      this.stopSeeMePromise = this.stopSeeMe(jid);
    }
  };
  handleStopVideoBroadcastingMessage = (message, from) => {
    const babyStation = this.getStationById(from);
    const { seeMeState, jid } = babyStation;

    if (from === jid && seeMeState === "on") {
      this.rtcManagers[jid].stopSendingVideoToBaby(from);
      this.openAlert(AlertsById.BABY_STOPPED_SEE_ME);
      this.updateStation(jid, {
        seeMeState: "off",
        localMediaStream: null
      });
    }
  };
  /* END See Me END */

  /* Push to Talk */
  togglePushToTalk = async (stationId) => {
    const { primaryStation } = this.state;
    let station = stationId ? this.getStationById(stationId) : primaryStation;
    if (!station) station = primaryStation;
    const { pushToTalkState, opusRecorder } = station;
    const { app } = this.props;

    if (pushToTalkState === "starting") return;

    if (pushToTalkState === "on") {
      opusRecorder.destroy();
      this.updateStation(station.jid, {
        pushToTalkState: "off",
        opusRecorder: null
      });
      return;
    }

    this.updateStation(station.jid, { pushToTalkState: "starting" });
    try {
      const status = await app.permissions.getMediaAccessStatus("microphone");
      let hasPermission = status === "granted";
      if (status === "not-determined") {
        hasPermission = await app.permissions.resolveMediaPermission("microphone");
      }

      if (!hasPermission) {
        status === "denied" && app.permissions.openDialog("microphone");
        this.updateStation(station.jid, { pushToTalkState: "off" });
        return;
      }
    } catch (error) {
      log.error("failed to resolve permissions", error);
      this.openAlert(AlertsById.GENERIC_FAILURE);
      this.updateStation(station.jid, {
        pushToTalkState: "off",
        opusRecorder: null
      });
      return;
    }

    try {
      const opusRecorder = await this.rtcManagers[station.jid].initializeAudioTransmission();
      this.updateStation(station.jid, {
        opusRecorder,
        pushToTalkState: "on"
      });
    } catch (error) {
      log.error("failed to initialize audio transmission", error);
      this.openAlert(AlertsById.GENERIC_FAILURE);
      this.updateStation(station.jid, {
        pushToTalkState: "off",
        opusRecorder: null
      });
      return;
    }
  };
  toggleSeeMeButton = () => {
    this.setState(({ renderSeeMeButton }) => ({
      renderSeeMeButton: !renderSeeMeButton
    }));
  };

  /* COMMANDS */
  playCommand = async (commandId) => {
    const { primaryStation } = this.state;

    this.updateStation(primaryStation.jid, {
      commandPlayerState: { commandId, playing: false, currentTime: 0 }
    });
    const request = RPCRequests.PlayCommand.create({ commandId });
    try {
      const { playerState } = await this.rpcs[primaryStation.jid].call(request, primaryStation.jid);
      if (playerState.commandId === commandId) {
        this.updateStation(primaryStation.jid, {
          commandPlayerState: playerState
        });
      }
    } catch (error) {
      log.error(error);
      this.openAlert(AlertsById.PLAY_COMMAND_FAILURE);
      this.updateStation(primaryStation.jid, {
        commandPlayerState: {}
      });
    }
  };
  stopCommand = () => {
    const { primaryStation } = this.state;
    const { jid } = primaryStation;
    const { messenger } = this.props;
    this.updateStation(jid, { commandPlayerState: {} });
    const message = Message.StopCommandMessage.create();
    messenger.sendAsEnvelope({ to: jid, payload: { message } });
  };
  resetCommandPlayer = () => {
    const { primaryStation } = this.state;
    this.updateStation(primaryStation.jid, { commandPlayerState: {} });
  };
  /* COMMANDS END */

  handleActivityChange = (stationId, newActivity) => {
    const babyStation = this.getStationById(stationId);
    const stateSelector = babyStation.activity === Activities.AWAKE ? "lastNoise" : "lastMotion";

    if (newActivity === Activities.SLEEPING) {
      this.updateStation(stationId, {
        activity: newActivity,
        [stateSelector]: Date.now()
      });
    } else this.updateStation(stationId, { activity: newActivity });
  };

  handleDataRemoteSettingsMessage = (remoteSettings, from) => {
    const { babyStations } = this.state;
    const { setDefaultAvatar } = this.props;
    const { avatar } = remoteSettings;
    if (babyStations[from]?.jid !== from) return;
    if (this.isBabyStationFamilyDevice({ station: babyStations[from] }) && avatar) {
      setDefaultAvatar(avatar.objectId);
    }
    this.updateStation(from, {
      ...this.state.remoteSettings,
      remoteSettings,
      avatarId: avatar.objectId
    });
  };

  handleAvatarChange = async (avatarId) => {
    const { primaryStation } = this.state;
    const { remoteSettings } = primaryStation;
    const { messenger, setDefaultAvatar } = this.props;

    setDefaultAvatar(avatarId);
    await this.updateStation(primaryStation.jid, { avatarId }, true);
    this.checkForMultiRoom({});
    const message = Message.DataRemoteSettings.create({
      avatar: { objectId: avatarId },
      micSensitivity: remoteSettings.micSensitivity,
      motionDetectionSensitivity: remoteSettings.motionDetectionSensitivity
    });
    messenger.sendAsEnvelope({ to: primaryStation.jid, payload: { message } });
  };

  updateBatteryStatus = (batteryStatus, from) => {
    if (batteryStatus?.state !== "CHARGING") {
      this.checkForLowBattery(from, batteryStatus?.level);
    }
    const { babyStations } = this.state;
    if (!babyStations[from]) return;
    this.updateStation(from, { batteryStatus });
  };

  checkForLowBattery = (jid, batteryLevel) => {
    const { babyStations, renderLowBatteryDialog } = this.state;
    const babyStation = babyStations[jid];
    const hadLowBattery = babyStation?.hadLowBattery || false;
    if (!batteryLevel) return;
    if (hadLowBattery && batteryLevel > 10) {
      this.updateStation(jid, { hadLowBattery: false });
      return;
    }
    if (hadLowBattery && batteryLevel < 10) return;
    if (batteryLevel > 10) return;
    this.updateStation(jid, { hadLowBattery: true });
    if (!renderLowBatteryDialog) {
      const isOlderThan10Seconds = Date.now() - babyStation?.monitoringStartTime > 10000;
      this.setState({
        lowBatteryDialogProps: {
          deviceName: babyStation?.name,
          avatarId: babyStation?.avatarId,
          playAlert: isOlderThan10Seconds
        }
      });
      this.toggleLowBatteryDialog();
    }
  };

  toggleLowBatteryDialog = () => {
    this.setState(({ renderLowBatteryDialog }) => ({
      renderLowBatteryDialog: !renderLowBatteryDialog
    }));
  };

  toggleMute = () => {
    const { babyStations, muted } = this.state;
    Object.values(babyStations).forEach((station) => {
      this.setVolume(station.jid, muted ? 1 : 0);
    });
    this.setState({ muted: !muted });
  };

  setVolume = (jid, volume) => {
    this.rtcManagers[jid].setPeerVolume(volume, jid);
  };

  handleMicSensitivityChange = (micSensitivity) => {
    const { primaryStation } = this.state;
    const { remoteSettings, avatarId } = primaryStation;
    const { messenger, defaultAvatar } = this.props;
    const avatar = avatarId ? { objectId: avatarId } : defaultAvatar;

    const message = Message.DataRemoteSettings.create({
      avatar,
      micSensitivity,
      motionDetectionSensitivity: remoteSettings.motionDetectionSensitivity
    });
    messenger.sendAsEnvelope({ to: primaryStation.jid, payload: { message } });

    this.updateStation(primaryStation.jid, {
      remoteSettings: { ...remoteSettings, micSensitivity }
    });
  };

  handleMotionSensitivityChange = (motionSensitivity) => {
    const { primaryStation } = this.state;
    const { remoteSettings, avatarId } = primaryStation;

    const { messenger, defaultAvatar } = this.props;
    const avatar = avatarId ? { objectId: avatarId } : defaultAvatar;

    const message = Message.DataRemoteSettings.create({
      avatar,
      micSensitivity: remoteSettings.micSensitivity,
      motionDetectionSensitivity: motionSensitivity
    });

    messenger.sendAsEnvelope({ to: primaryStation.jid, payload: { message } });

    this.updateStation(primaryStation.jid, {
      remoteSettings: {
        ...remoteSettings,
        motionDetectionSensitivity: motionSensitivity
      }
    });
  };

  handleLowLightModeChange = async (lowLightMode) => {
    try {
      const { primaryStation } = this.state;
      const request = RPCRequests.SetLowLightMode.create(lowLightMode);
      const response = await this.rpcs[primaryStation.jid].call(request, primaryStation.jid);

      const { result, cameraAndFlashlightState } = response;
      if (result === "SUCCESS") {
        this.updateStation(primaryStation.jid, { cameraAndFlashlightState });
      } else {
        this.openAlert(AlertsById.GENERIC_FAILURE);
      }
    } catch (err) {
      log.error(err);
      this.openAlert(AlertsById.GENERIC_FAILURE);
    }
  };

  openAlert = (id, props = {}) => {
    props.onDismiss = props.onDismiss || this.dismissAlert;
    this.setState({
      alert: {
        id,
        props
      }
    });
  };
  dismissAlert = () => this.setState({ alert: { id: null, props: {} } });

  toggleTilesView = () => {
    this.setState(({ showTilesView }) => ({ showTilesView: !showTilesView }));
  };

  isBabyStationFamilyDevice = ({ station, jid }) => {
    if (jid) {
      const { babyStations } = this.state;
      return babyStations[jid]?.group === "myDevices";
    } else {
      return station?.group === "myDevices";
    }
  };

  getMonitoredAvatar = () => {
    const { primaryStation } = this.state;
    const { allAvatars } = this.props;
    const avatar = allAvatars[primaryStation.avatarId] || this.props.defaultAvatar;
    return this.isBabyStationFamilyDevice({ station: primaryStation }) ? avatar : null;
  };

  togglePurchaseFlow = (initialContent) => {
    const { purchase } = this.props;
    let content = initialContent;
    if (typeof initialContent !== "string") content = null;
    purchase.open({
      content,
      props: {
        isMonitoring: () => Object.keys(this.state.babyStations).length !== 0,
        stopMonitoring: this.stopMonitoringForAll
      }
    });
  };

  resetStation = (stationId) => {
    const { babyStations } = this.state;
    debug.parentStation("reseting station ", stationId);
    this.updateStation(stationId, { status: "disconnecting" });

    monitoringRestorer.removeRecoverableSession(stationId);

    this.clearStartVideoConnectionChecker && this.clearStartVideoConnectionChecker();
    this.clearStartVideoConnectionChecker = null;

    this.clearDataChannleConnectionChecker && this.clearDataChannleConnectionChecker();
    this.clearDataChannleConnectionChecker = null;

    this.stopSeeMePromise = null;
    this.peerToPeerMessenger.removeListener(
      stationId,
      Message.BabyStationState.name,
      this.handleBabyStationStateMessage
    );
    this.peerToPeerMessenger.removeListener(stationId, Message.BatteryStatus.name, this.updateBatteryStatus);
    this.peerToPeerMessenger.removeListener(
      stationId,
      Message.DataRemoteSettings.name,
      this.handleDataRemoteSettingsMessage
    );
    this.peerToPeerMessenger.removeListener(
      stationId,
      Message.StopVideoBroadcasting.name,
      this.handleStopVideoBroadcastingMessage
    );
    this.peerToPeerMessenger.removeListener(
      stationId,
      Message.CameraAndFlashlightState.name,
      this.handleCameraAndFlashlightStateMessage
    );

    if (this.pingServices[stationId]) {
      this.pingServices[stationId].stop();
      this.pingServices[stationId] = null;
      delete this.pingServices[stationId];
    }
    if (this.errorManagers[stationId]) {
      this.errorManagers[stationId].destroy();
      this.errorManagers[stationId] = null;
    }
    if (this.videoErrorManagers[stationId]) {
      this.videoErrorManagers[stationId].destroy();
      this.videoErrorManagers[stationId] = null;
    }
    if (babyStations[stationId] && babyStations[stationId].flashlightManager) {
      babyStations[stationId].flashlightManager.destroy();
      this.updateStation(stationId, { flashlightManager: null });
    }
    this.activityEmitters[stationId] && this.activityEmitters[stationId].destroy();
    this.activityEmitters[stationId] = null;
    delete this.activityEmitters[stationId];

    this.rpcs[stationId] && this.rpcs[stationId].destroy();
    this.rpcs[stationId] = null;
    delete this.rpcs[stationId];

    if (this.noiseMeters[stationId]) {
      this.noiseMeters[stationId].destroy();
      this.noiseMeters[stationId] = null;
      delete this.noiseMeters[stationId];
    }
    // this.isWaitingForStartVideoResponse = false;
    // this.isWaitingForStopVideoResponse = false;

    if (babyStations[stationId] && babyStations[stationId].opusRecorder) {
      babyStations[stationId].opusRecorder.destroy();
    }
    if (this.rtcManagers[stationId]) {
      this.rtcManagers[stationId].removeListener("media-track", this.onTrack);
      this.rtcManagers[stationId].destroy();
      this.rtcManagers[stationId] = null;
      delete this.rtcManagers[stationId];
      this.peerConnection = null;
    }

    appDebugger.parentRtcManagers = this.rtcManagers;
    const stations = { ...babyStations };
    this.setState({
      previousBabyStation: stations[stationId]
    });
    delete stations[stationId];
    this.setState({ babyStations: stations });

    if (Object.keys(stations).length > 1) {
      this.initializePrimaryStation(stations);
    }
    if (Object.keys(stations).length === 1) {
      const primaryStation = Object.values(stations).flatMap((station) => station)[0];
      this.updatePrimaryStation(primaryStation);
    }
  };

  render() {
    const { babyStationSelect, multiBabyManager, alert, hasConnectedStations } = this.state;
    const { isStopLullabyPlayerDialogOpen } = this.state;
    const { isInDetachMode, lullabyPlayers } = this.props;

    return (
      <ParentStationContext.Provider value={this.state}>
        {multiBabyManager.isOpen && environment.supportsMultibaby && hasConnectedStations && !isInDetachMode && (
          <MultiBabyManager />
        )}
        {babyStationSelect.isOpen && <SelectBabyStation />}
        {hasConnectedStations && <Monitors lullabyPlayers={lullabyPlayers} />}

        {environment.supportsLullabies && (
          <StopLullabyPlayerDialog
            isOpen={isStopLullabyPlayerDialogOpen}
            onDismiss={this.dismissStopLullabyPlayerDialog}
          />
        )}

        <Alert id={alert.id} props={alert.props} />
        {hasConnectedStations && <MonitoringHelp />}
      </ParentStationContext.Provider>
    );
  }
}
